import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import {
  AlertBannerContext,
  API_RESPONSE_CODE,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import { registrationQuery } from "../../Graphql/querries/registrationQuery";
import {
  RegisterRequest,
  RegisterResponse
} from "../../Graphql/types/register";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

const useCreateAccount = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<RegisterResponse, Error, RegisterRequest>({
    mutationKey: ["register"],

    mutationFn: (input) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started creating account",
        buildLogContext(
          "auth",
          "create-account",
          // userId/companyId/storeId are not yet known at registration time
          undefined,
          undefined,
          undefined
        )
      );

      return fetchGraphQL(registrationQuery, { input }, setAlert);
    },

    onSuccess: (response) => {
      const { code } = response.register;

      // ⚠️ Expected API/business failure — API responded but rejected the registration
      if (code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Create account rejected by API",
          buildLogContext(
            "auth",
            "create-account",
            // userId/companyId/storeId are not assigned until after successful registration
            undefined,
            undefined,
            undefined,
            { apiCode: String(code) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: code as unknown as ALERT_BANNER_CODE
        });
        return;
      }

      // ✅ Success
      sendAppLog(
        LOG_LEVEL.INFO,
        "Account created successfully",
        buildLogContext(
          "auth",
          "create-account",
          // userId/companyId/storeId are not yet populated in the register response
          undefined,
          undefined,
          undefined
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
        code: ALERT_BANNER_CODE.REGISTRATION_SUCCESS
      });
    },

    onError: (error) => {
      // ❌ Unexpected runtime/network error
      captureAppException(
        "Registration failed",
        error,
        buildLogContext(
          "auth",
          "create-account",
          // userId/companyId/storeId are not available — registration never completed
          undefined,
          undefined,
          undefined
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: error.message
          ? (error.message as ALERT_BANNER_CODE)
          : ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

export default useCreateAccount;
